import * as THREE from 'three';
import type { Signal, SignalKind, SignalLine } from '@somnhold/protocol';
import { simToWorld, type Vfx } from '@somnhold/render';
import { isRevealed, type BattleState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { isTouch } from '../settings.ts';
import { h, viewH, viewW } from '../ui/dom.ts';

const g = (body: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

/** Each signal's glyph and colour (the dragon red, a cold blue, pale stone, green; help in the light red, beating). */
export const SIGNAL_LOOK: Record<SignalKind, { icon: string; color: string }> = {
  attack: { icon: g('<path d="M4 4l11 11M4 4h4M4 4v4M20 4L9 15M20 4h-4M20 4v4M7 13l-3 3 4 4 3-3M17 13l3 3-4 4-3-3"/>'), color: '#ff4f3a' },
  defend: { icon: g('<path d="M12 3l7.5 3v5.2c0 5-3.2 8.2-7.5 9.8-4.3-1.6-7.5-4.8-7.5-9.8V6z"/><path d="M12 7.5v9M8.5 11.5h7"/>'), color: '#7cc4ff' },
  danger: { icon: g('<path d="M12 3.5L21.5 20h-19z"/><path d="M12 10v4.5M12 17.4v.1"/>'), color: '#eceef2' },
  coming: { icon: g('<path d="M4 12h12M11 6l6 6-6 6"/><path d="M20 5v14"/>'), color: '#7fd36a' },
  help: { icon: g('<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5v9M7.5 12h9"/>'), color: '#ff8a6e' },
};
/** Around the wheel: up, left, right, down; help in the middle. */
const PETALS: { kind: SignalKind; dx: number; dy: number }[] = [
  { kind: 'attack', dx: 0, dy: -1 },
  { kind: 'defend', dx: -1, dy: 0 },
  { kind: 'coming', dx: 1, dy: 0 },
  { kind: 'danger', dx: 0, dy: 1 },
];
const SFX = { attack: 'sigAttack', defend: 'sigDefend', danger: 'sigDanger', coming: 'sigComing', help: 'sigHelp' } as const;
/** How long a signal stays (ms), the last part fading. */
const LIFE_MS = 6000;
const HELP_MS = 8000;
const FADE_MS = 600;
/** At most this many at once: the oldest goes. */
const MAX_SHOWN = 6;
/** Held this long, the signal button calls for help without opening the wheel. */
const HOLD_HELP_MS = 450;
/** The wheel's petals sit this far from its middle (px); a mouse nearer than DEAD stays in the middle. */
const RADIUS = 62;
const DEAD = 22;

/** Where a signal goes: a spot on the board, and the nightmare or hero there when there is one. */
export interface SignalSpot {
  x: number;
  y: number;
  enemy: number;
  hero: number;
}

interface Shown {
  line: SignalLine;
  until: number;
  el: HTMLElement;
  arrow: HTMLElement;
  /** Where it was last drawn (a followed nightmare that died leaves it there). */
  at: THREE.Vector3;
  pulse: number;
  edge: boolean | null;
  side: string;
  /** How high over the spot it floats: over a hero, above its name. */
  lift: number;
}

export interface SignalsDeps {
  me: string;
  names: Record<string, string>;
  state: () => BattleState;
  send: (s: Signal) => void;
  /** What lies under a screen point (null: off the board). */
  spotAt: (x: number, y: number) => SignalSpot | null;
  /** A tap on a signal at the edge walks the hero there. */
  walkTo: (x: number, y: number) => void;
  vfx: Vfx;
  /** Something to say small at the top (where to tap). */
  hint: (msg: string | null) => void;
  /** A help call lights the caller's frame in the party list. */
  flag: (pid: string) => void;
  /** A line in the chat's log, so whoever looked away knows what happened. */
  note: (pid: string, text: string, color: string) => void;
  /** Said once ever, the first time the button shows: how to signal. */
  tip: (msg: string) => void;
}

/**
 * Signals for the group ("attack here", "defend here", "watch out", "on my way", "help!"): a wheel to pick
 * one (the button on a phone, G held or Alt + click with a mouse), a mark where it was put (following the
 * nightmare or hero it was put on), and an arrow at the edge of the screen when it is out of sight.
 * They go through the room, outside the fight: nothing here touches the simulation.
 */
export class Signals {
  /** The marks over the board and at its edges. */
  readonly layer = h('div.sig-layer');
  /** The signal button and its wheel (bottom right, by the powers). */
  readonly el: HTMLElement;
  private btn: HTMLButtonElement;
  private wheel: HTMLElement;
  private alarm = h('div.sig-alarm');
  /** A kind picked from the wheel, waiting for the tap that says where. */
  pending: SignalKind | null = null;
  /** The wheel held open at the mouse (G): its middle on screen and the spot under it. */
  private cursor: { x: number; y: number; spot: SignalSpot | null; pick: SignalKind | null } | null = null;
  private shown: Shown[] = [];
  private sent: number[] = [];
  private holdTimer = 0;
  private v = new THREE.Vector3();

  constructor(private d: SignalsDeps) {
    this.btn = h('button.sig-btn', { type: 'button', 'data-test': 'signal-btn', 'aria-label': t('signal.button'), title: isTouch() ? '' : t('signal.btnTip') }, h('span.sig-btn-ico', { html: SIGNAL_LOOK.attack.icon }), isTouch() ? null : h('span.key', {}, 'G'));
    this.wheel = h('div.sig-wheel', { role: 'menu', 'aria-label': t('signal.button') });
    for (const p of PETALS) this.wheel.append(this.petal(p.kind, p.dx, p.dy));
    this.wheel.append(this.petal('help', 0, 0));
    this.el = h('div.sig', { 'data-test': 'signals' }, this.wheel, this.btn);
    this.layer.append(this.alarm);
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      window.clearTimeout(this.holdTimer);
      if (this.pending || this.isOpen) return;
      // Held: help, straight away (when you're being overrun there is no time for the wheel).
      this.btn.classList.add('charging');
      this.holdTimer = window.setTimeout(() => {
        this.holdTimer = 0;
        this.btn.classList.remove('charging');
        this.btn.dataset.held = '1';
        navigator.vibrate?.(20);
        this.help();
      }, HOLD_HELP_MS);
    });
    const letGo = (): void => {
      if (this.holdTimer) window.clearTimeout(this.holdTimer);
      this.holdTimer = 0;
      this.btn.classList.remove('charging');
    };
    this.btn.addEventListener('pointerup', letGo);
    this.btn.addEventListener('pointercancel', letGo);
    this.btn.addEventListener('pointerleave', letGo);
    this.btn.addEventListener('click', () => {
      if (this.btn.dataset.held) {
        delete this.btn.dataset.held;
        return;
      }
      if (this.pending || this.isOpen) this.cancel();
      else this.openWheel();
    });
  }

  private petal(kind: SignalKind, dx: number, dy: number): HTMLElement {
    const look = SIGNAL_LOOK[kind];
    const el = h(
      `button.sig-petal.${kind}`,
      { type: 'button', role: 'menuitem', 'data-kind': kind, 'aria-label': t(`signal.${kind}`), style: `--c:${look.color};--dx:${dx * RADIUS}px;--dy:${dy * RADIUS}px` },
      h('span.sig-petal-ico', { html: look.icon }),
      h('span.sig-petal-txt', {}, t(`signal.${kind}`)),
    );
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.cursor) return this.releaseCursor(kind);
      this.closeWheel();
      if (kind === 'help') return this.help();
      this.pending = kind;
      this.btn.classList.add('pending');
      this.btn.style.setProperty('--c', look.color);
      (this.btn.firstElementChild as HTMLElement).innerHTML = look.icon;
      this.d.hint(t(isTouch() ? 'signal.whereTouch' : 'signal.whereMouse'));
    });
    return el;
  }

  get isOpen(): boolean {
    return this.el.classList.contains('open');
  }

  /** Whether the button shows: only with someone else playing live (signals for ghosts would go nowhere). */
  setVisible(on: boolean): void {
    if (this.el.hidden === !on) return;
    this.el.hidden = !on;
    if (!on) return this.cancel();
    try {
      if (localStorage.getItem('somnhold.signalTip')) return;
      localStorage.setItem('somnhold.signalTip', '1');
    } catch {
      return;
    }
    window.setTimeout(() => !this.el.hidden && this.d.tip(t(isTouch() ? 'signal.tipTouch' : 'signal.tipMouse')), 2500);
  }

  private openWheel(): void {
    this.el.classList.add('open');
    this.wheel.style.removeProperty('transform');
  }

  private closeWheel(): void {
    this.el.classList.remove('open', 'at-cursor');
    this.wheel.querySelector('.hot')?.classList.remove('hot');
  }

  /** Back to nothing: the wheel shut, no kind waiting. */
  cancel(): void {
    this.closeWheel();
    this.cursor = null;
    if (this.pending) this.d.hint(null);
    this.pending = null;
    this.btn.classList.remove('pending');
    this.btn.style.removeProperty('--c');
    (this.btn.firstElementChild as HTMLElement).innerHTML = SIGNAL_LOOK.attack.icon;
  }

  /** The tap after picking a kind: that is where it goes. False when it fell off the board (still waiting). */
  placeAt(x: number, y: number): boolean {
    const kind = this.pending;
    if (!kind) return false;
    const spot = this.d.spotAt(x, y);
    if (!spot) return false;
    this.cancel();
    this.emit(kind, spot);
    return true;
  }

  /** Alt + click (or G let go without picking): the obvious one for what is there. */
  smart(x: number, y: number): void {
    const spot = this.d.spotAt(x, y);
    if (!spot) return;
    this.emit(this.guess(spot), spot);
  }

  private guess(spot: SignalSpot): SignalKind {
    if (spot.enemy) return 'attack';
    const mine = this.d.state().heroes.find((x) => x.pid === this.d.me);
    if (spot.hero && spot.hero === mine?.id) return 'help';
    return 'defend';
  }

  /** G pressed with the mouse over the board: the wheel opens right there. */
  openAtCursor(x: number, y: number): void {
    if (this.el.hidden) return;
    this.cancel();
    this.cursor = { x, y, spot: this.d.spotAt(x, y), pick: null };
    const r = this.btn.getBoundingClientRect();
    // The wheel lives by the button: moved over to the mouse.
    this.wheel.style.transform = `translate(${(x - (r.left + r.width / 2)).toFixed(0)}px, ${(y - (r.top + r.height / 2)).toFixed(0)}px)`;
    this.el.classList.add('open', 'at-cursor');
  }

  /** The mouse moves while G is held: the petal it points at lights up. */
  aim(x: number, y: number): void {
    const c = this.cursor;
    if (!c) return;
    const dx = x - c.x;
    const dy = y - c.y;
    let pick: SignalKind | null = null;
    if (Math.hypot(dx, dy) > DEAD) pick = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'defend' : 'coming') : dy < 0 ? 'attack' : 'danger';
    if (pick === c.pick) return;
    c.pick = pick;
    this.wheel.querySelector('.hot')?.classList.remove('hot');
    if (pick) this.wheel.querySelector(`[data-kind=${pick}]`)?.classList.add('hot');
  }

  /** G let go: the lit petal goes where the wheel opened; none lit, the obvious one for what is there. */
  releaseCursor(kind?: SignalKind): void {
    const c = this.cursor;
    if (!c) return;
    this.cancel();
    if (!c.spot) return;
    this.emit(kind ?? c.pick ?? this.guess(c.spot), c.spot);
  }

  get aimingCursor(): boolean {
    return !!this.cursor;
  }

  private help(): void {
    const s = this.d.state();
    const mine = s.heroes.find((x) => x.pid === this.d.me);
    if (!mine) return;
    this.cancel();
    this.emit('help', { x: mine.x, y: mine.y, enemy: 0, hero: mine.id });
  }

  private emit(kind: SignalKind, spot: SignalSpot): void {
    // The room lets a few through and then makes you wait: said here instead of sending into nothing.
    const now = performance.now();
    this.sent = this.sent.filter((at) => now - at < 4000);
    if (this.sent.length >= 3) {
      this.d.hint(t('signal.slow'));
      window.setTimeout(() => this.d.hint(null), 1600);
      return;
    }
    this.sent.push(now);
    const target = spot.enemy || spot.hero;
    this.d.send({ kind, x: Math.round(spot.x), y: Math.round(spot.y), ...(target ? { target } : {}) });
  }

  /** A signal from the room (mine included: the room echoes it). */
  receive(line: SignalLine): void {
    const now = performance.now();
    // The same one again from the same player replaces it; past the cap the oldest goes.
    const same = this.shown.findIndex((x) => x.line.pid === line.pid && x.line.kind === line.kind);
    if (same >= 0) this.drop(same);
    while (this.shown.length >= MAX_SHOWN) this.drop(0);
    const look = SIGNAL_LOOK[line.kind];
    const who = line.pid === this.d.me ? t('chat.you') : (this.d.names[line.pid] ?? t('chat.someone'));
    const arrow = h('span.sig-arrow');
    const el = h(
      `button.sig-mark.${line.kind}${line.kind === 'help' ? '.self' : ''}`,
      // Hidden until place() puts it where it points: it comes from the room between drawings, and the page could
      // paint it first at the screen's top left corner.
      { type: 'button', style: `--c:${look.color};visibility:hidden`, 'data-test': 'signal-mark', 'data-kind': line.kind, tabindex: -1 },
      arrow,
      h('span.sig-badge', { html: look.icon }),
      h('span.sig-who', {}, who),
    );
    el.addEventListener('pointerdown', (e) => e.stopPropagation());
    el.addEventListener('click', () => {
      const item = this.shown.find((x) => x.el === el);
      if (item?.edge) this.d.walkTo(item.line.x, item.line.y);
    });
    this.layer.append(el);
    const at = this.followed(line) ?? simToWorld(line.x, line.y);
    const item: Shown = { line, until: now + (line.kind === 'help' ? HELP_MS : LIFE_MS), el, arrow, at: at.clone(), pulse: 0, edge: null, side: '', lift: line.target && this.d.state().heroes.some((x) => x.id === line.target) ? 2.8 : 2.1 };
    this.shown.push(item);
    // On the ground: a shaft of light and a ring, then a ring now and again while it lasts (pooled effects).
    const base = item.at.clone().setY(0.05);
    this.d.vfx.shaft(base, base.clone().setY(line.kind === 'help' ? 7 : 5), look.color, 16, line.kind === 'help' ? 0.7 : 0.5);
    this.d.vfx.ring(base, 0.2, 1.6, look.color, 10, 1);
    audio.play(SFX[line.kind], line.pid === this.d.me ? 0.6 : 1);
    const text = t(`signal.${line.kind}`);
    const on = this.targetName(line);
    this.d.note(line.pid, `${text}${on ? ` · ${on}` : ''}`, look.color);
    if (line.kind === 'help') {
      this.d.flag(line.pid);
      if (line.pid !== this.d.me) {
        this.alarm.classList.remove('on');
        void this.alarm.offsetWidth;
        this.alarm.classList.add('on');
        navigator.vibrate?.([30, 60, 30]);
      }
    }
  }

  /** What it was put on, said in the chat's line: a nightmare by its kind, a hero by name. */
  private targetName(line: SignalLine): string {
    if (!line.target) return '';
    const s = this.d.state();
    const e = s.enemies.find((x) => x.id === line.target);
    if (e) return t(`enemy.${e.kind}`);
    const hero = s.heroes.find((x) => x.id === line.target);
    if (!hero) return '';
    return hero.pid === this.d.me ? t('chat.you') : (this.d.names[hero.pid] ?? hero.name);
  }

  private positions = new Map<number, THREE.Vector3>();
  /** Where the marks went this frame (reused), so the next ones step aside. */
  private spots: { x: number; y: number }[] = [];

  /** Where the one it follows is drawn now (null: nobody, or gone). */
  private followed(line: SignalLine): THREE.Vector3 | null {
    if (!line.target) return null;
    const s = this.d.state();
    const e = s.enemies.find((x) => x.id === line.target);
    // Dead, or gone out of sight (a stalker): the mark stays where it was last seen.
    if (e && (e.hp <= 0 || !isRevealed(s, e))) return null;
    const hero = e ? null : s.heroes.find((x) => x.id === line.target);
    if (!e && !hero) return null;
    return this.positions.get(line.target) ?? null;
  }

  private drop(i: number): void {
    const [item] = this.shown.splice(i, 1);
    item?.el.remove();
  }

  /** Every frame: each mark over its spot, or held at the edge pointing to it. Nothing to do when there are none. */
  place(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    if (this.shown.length === 0) return;
    this.positions = positions;
    const now = performance.now();
    const W = viewW();
    const H = viewH();
    // Clear of the chips along the top and the powers along the bottom.
    const m = 34;
    const top = 110;
    const bottom = H - (isTouch() ? 150 : 110);
    let placed = 0;
    for (let i = this.shown.length - 1; i >= 0; i--) {
      const it = this.shown[i] as Shown;
      if (now >= it.until) {
        this.drop(i);
        continue;
      }
      const p = this.followed(it.line);
      if (p) it.at.copy(p);
      it.el.classList.toggle('fading', it.until - now < FADE_MS);
      // A ring on the ground now and again while it lasts, faster for help.
      if (now >= it.pulse) {
        it.pulse = now + (it.line.kind === 'help' ? 650 : 1100);
        this.d.vfx.ring(this.v.copy(it.at).setY(0.05), 0.3, it.line.kind === 'help' ? 1.8 : 1.3, SIGNAL_LOOK[it.line.kind].color, 9, 0.85);
      }
      this.v.copy(it.at).setY(it.at.y + it.lift).project(camera);
      const behind = this.v.z > 1;
      let x = (this.v.x * 0.5 + 0.5) * W;
      let y = (-this.v.y * 0.5 + 0.5) * H;
      if (behind) {
        x = W - x;
        y = H - y;
      }
      const inView = !behind && x > m && x < W - m && y > top && y < bottom;
      if (it.edge !== !inView) {
        it.edge = !inView;
        it.el.classList.toggle('edge', it.edge);
        it.el.tabIndex = it.edge ? 0 : -1;
      }
      let turn = 0;
      if (!inView) {
        const cx = W / 2;
        const cy = (top + bottom) / 2;
        const dx = x - cx;
        const dy = y - cy;
        const k = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), ((bottom - top) / 2) / Math.max(1, Math.abs(dy)));
        x = cx + dx * k;
        y = cy + dy * k;
        turn = Math.atan2(dy, dx);
      }
      // Two marks in the same place (several at one edge, or close on the board): the later ones step aside.
      for (let tries = 0; tries < MAX_SHOWN; tries++) {
        let hit = false;
        for (let k = 0; k < placed; k++) {
          const q = this.spots[k] as { x: number; y: number };
          // At the edge a mark carries its name under it: taller.
          if (Math.abs(q.x - x) < 42 && Math.abs(q.y - y) < (inView ? 42 : 62)) hit = true;
        }
        if (!hit) break;
        if (inView) y -= 44;
        else if (x <= m + 1 || x >= W - m - 1) y += y > (top + bottom) / 2 ? -64 : 64;
        else x += x > W / 2 ? -46 : 46;
      }
      const spot = this.spots[placed] ?? { x: 0, y: 0 };
      spot.x = x;
      spot.y = y;
      this.spots[placed++] = spot;
      const side = !inView && x < W * 0.3 ? 'l' : !inView && x > W * 0.7 ? 'r' : '';
      if (side !== it.side) {
        it.side = side;
        it.el.classList.toggle('side-l', side === 'l');
        it.el.classList.toggle('side-r', side === 'r');
      }
      it.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`;
      if (it.edge) it.arrow.style.transform = `rotate(${turn.toFixed(3)}rad)`;
      if (it.el.style.visibility) it.el.style.visibility = '';
    }
  }

  dispose(): void {
    window.clearTimeout(this.holdTimer);
    this.layer.remove();
    this.el.remove();
  }
}
